/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import { FormLabel, LinearProgress, Popover, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import IconButton from '@mui/material/IconButton';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { usePostRequest } from '../../../../../../../api/helper';
import { ARGOS_PENDING_COLOR, ARGOS_PROGRESS_COLOR, headerGray } from '../../../../../../../constants/colors';
import VAR_COMMON_CONSTANTS from "../../../../../../../constants/globalVariables";
import { theme } from '../../../../../../../constants/theme';
import { ETrackedItemTypes } from "../../../../../../../constants/trackedItems";
import { useHasWriteAccess } from '../../../../../../../redux/ducks/AccessControl/accessControlHelpers';
import {
    editDatasetLocalState,
    EEditDatasetLocalStateActionTypes,
    selectCoverageRequirementsDataState,
    selectDatasetsExecutionStatus,
    selectDatasetsLocalState,
    selectDialogState,
    selectIsCancellingDatasets,
    selectIsCoverageRequirementsSaved,
    selectRedlineDataState,
    selectTriggerDoeMinMaxViolations,
    setIsDownloadAsCSVDialogOpen
} from '../../../../../../../redux/ducks/CoverageRequirements/slice';
import { useAppDispatch, useAppSelector } from '../../../../../../../redux/hooks';
import ArgosButton from '../../../../../../components/Buttons/ArgosButton';
import { ArgosFavoriteIconLogic } from "../../../../../../components/Buttons/ArgosFavoriteIconLogic";
import ArgosFlexbox from '../../../../../../components/Containers/ArgosFlexbox';
import { StyledArgosDialog } from '../../../../../../components/Dialogs/ArgosDialog';
import { DownloadDatasetsDialog } from '../../../../../../components/Dialogs/RedlineAppDialogs/DownloadDatasetsDialog';
import Loader from '../../../../../../components/Loader/Loader';
import { COVERAGE_REQUIREMENTS_CONSTANTS } from "../../../../constants";
import { EDatasetExecutionStatus, EXECUTION_STATUSES, IN_PROGRESS_STATUSES, MERGED_DATASET_TYPE_STR, SINGLE_DATASET_TYPE_STR } from '../../../constants';

interface IProps {
  datasetId: string
  datasetType: string
  datasetName: string
  datasetOwner: string
  datasetPath: string[]
  executionStatus: EDatasetExecutionStatus
  handleDownloadDatasetFile: any
  saveToDatabase: any
  onClickAbort: any
  datasetChange?: any
  setDatasetChange?: any
  studyId: string
  isReadOnlyStudy?: boolean
}

const DatasetControlPanel = (props: IProps) => {
  const isReadOnlyStudy: boolean | undefined = props?.isReadOnlyStudy
  const paths = props.datasetPath
  const dispatch = useAppDispatch()
  const hasWriteAccess = useHasWriteAccess()
  const isDownloadAsCSVDialogOpen = useAppSelector(state => selectDialogState(state).isDownloadAsCSVDialogOpen)
  const isCoverageRequirementsSaved = useAppSelector(selectIsCoverageRequirementsSaved)
  const _isCancellingDatasetMap: any = useAppSelector(selectIsCancellingDatasets)
  const shouldShowCancellingDataset: any = _isCancellingDatasetMap != null ? _isCancellingDatasetMap[props.datasetId] : false
  const selectedDatasetExecutionStatus: EDatasetExecutionStatus = useAppSelector((state) => selectDatasetsExecutionStatus(state)?.[props?.datasetId]) ?? ''
  const doeMinMaxViolations = useAppSelector(selectTriggerDoeMinMaxViolations);

  const [owner, setOwner] = useState<string>("")
  const [isLoadingOwner, setIsLoadingOwner] = useState<boolean>(false)
  const [didFailToLoadOwner, setDidFailToLoadOwner] = useState<boolean>(false)
  const [ownerNotFound, setOwnerNotFound] = useState<boolean>(false)

  const breadcrumbs: { title: string }[] = useMemo(() => {
    if (!paths) return []
    return paths.map((p) => {
        return {
          title: `${p}`
        }
      }
    )
  }, [paths])
  const fetchHandle = usePostRequest("/HttpTriggerGetUser");

  useEffect(() => {
    if (props.datasetOwner == null) return;
    setIsLoadingOwner(true)
    setOwner("")
    fetchHandle(undefined, undefined, undefined, `?gmid=${props.datasetOwner}`)
      .then(data => {
        if(data.userNotFound) {
          setOwnerNotFound(true)
        } else {
          setOwnerNotFound(false)
          setOwner(data.firstName + " " + data.lastName)
        }
        setDidFailToLoadOwner(false)
      }).catch((e) => {
      console.error(e)
      setDidFailToLoadOwner(true)
    }).finally(() => {
      setIsLoadingOwner(false)
    })
  }, [props.datasetOwner])

  useEffect(() => {
    addCancel()
  }, [selectedDatasetExecutionStatus])


  const downloadDatasetAsCSV = (fileName: string,red:boolean,raw:boolean) => {
    props?.handleDownloadDatasetFile(fileName,red,raw)
  }
  const handleOpenDownloadAsCSV = () => {
    dispatch(setIsDownloadAsCSVDialogOpen(true))
  }
  const onClose = () => {
    dispatch(setIsDownloadAsCSVDialogOpen(false))
  }

  const currentCoverageRequirementsDataRunData = useAppSelector(state => selectCoverageRequirementsDataState(state)?.runData ?? null)
  const currentCoverageRequirementsData = useAppSelector(state => selectCoverageRequirementsDataState(state) ?? null)

  const currentDatasetLocalState = useAppSelector((state) => selectDatasetsLocalState(state)?.[props.datasetId])
  useEffect(() => {
    if(!currentCoverageRequirementsDataRunData) return;
    if(props.datasetId !== currentCoverageRequirementsData.id) return;
    if(selectedDatasetExecutionStatus === EDatasetExecutionStatus.ABORTED || selectedDatasetExecutionStatus === EDatasetExecutionStatus.COMPLETED) {
      const {progress, totalSteps, currentStep} = currentCoverageRequirementsDataRunData;
      const fraction = totalSteps !== 0 ? `${currentStep} / ${totalSteps}` : ' ';
      const datasetIdsToData = {
        [props.datasetId]: {progress, totalSteps, currentStep, fraction}
      }
      dispatch(editDatasetLocalState({type: EEditDatasetLocalStateActionTypes.MULTIPLE, data: datasetIdsToData}));
    }
  }, [currentCoverageRequirementsDataRunData, selectedDatasetExecutionStatus, props.datasetId]);

  const getProgressFromRedlineData = (_redlineData: any) => {
    if (_redlineData == null) return 0
    let progress = 0
    if (_redlineData?.totalSteps != undefined && _redlineData?.totalSteps != 0) {
      progress = Math.floor((_redlineData?.progressCount / _redlineData?.totalSteps) * 100)
    } else {
      progress = 0
    }
    return progress
  }

  /**
   * useMemo with redlineData dep
   */
  const redlineDataState = useAppSelector(selectRedlineDataState)
  const generateDatasetProgressData = useMemo(() => {
    const shouldReplaceExecutionStatusWithLoader = redlineDataState.isLoading && EXECUTION_STATUSES.has(props.executionStatus)
    const datasetAnalysisProgress = getProgressFromRedlineData(redlineDataState.data)
    const _totalNumberOfRuns = redlineDataState.data?.totalSteps
    const _currentRun = redlineDataState.data?.progressCount
    const areTotalNumberOfRunsAndCurrentRunValid = _totalNumberOfRuns != null && _currentRun != null
    let analysisProgressFractionString = ' '
    if (_totalNumberOfRuns !== 0) {
      analysisProgressFractionString = `${_currentRun} / ${_totalNumberOfRuns}`
    }

    return {
      shouldReplaceExecutionStatusWithLoader,
      datasetAnalysisProgress,
      areTotalNumberOfRunsAndCurrentRunValid,
      _totalNumberOfRuns,
      _currentRun,
      analysisProgressFractionString
    }
  }, [redlineDataState])

  const selectedDatasetId = props.datasetId

  const favoriteIcon = useMemo(() =>
    <ArgosFavoriteIconLogic favoriteType={ETrackedItemTypes.COVERAGE_REQUIREMENTS_DATASET} trackedItemUid={selectedDatasetId} partitionKey={selectedDatasetId}/>
    ,[selectedDatasetId])
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const fetchHandleCancelRuns = usePostRequest(COVERAGE_REQUIREMENTS_CONSTANTS.details.endpoints.cancelRawRedlineRuns)
  const cancelRun = (datasetId: string) => {
    fetchHandleCancelRuns({
      studyId: props.studyId,
      datasetIds: [datasetId]
    }).then(() => {
    }).catch((e) => {
      console.log(e)
      toast.error('Failed to cancel run')
    })
  }

  const open = Boolean(anchorEl);
  const allowCancel = IN_PROGRESS_STATUSES.has(props.executionStatus)

  const [allowCancelOption, setAllowCancelOption] = useState(false)
  const [statusColor, setStatusColor] = useState<string>()

  const getExecutionStatusTitle = (status: EDatasetExecutionStatus) => {
    if(status === EDatasetExecutionStatus.INVALID_RESULTS) return 'Invalid'
    return status
  }
  const statusTitle = "Execution Status: " + getExecutionStatusTitle(selectedDatasetExecutionStatus)
  const [activeLoader, setActiveLoader] = useState<boolean>(false)

  useEffect(() => {
    loaderStatus()
  }, [selectedDatasetExecutionStatus])

  const loaderStatus = () => {
    if (selectedDatasetExecutionStatus === 'Pending' || selectedDatasetExecutionStatus === "In Progress") {
      setActiveLoader(true)
    }
    else{
      setActiveLoader(false)
    }
  }

  const addCancel = () => {
    if (selectedDatasetExecutionStatus === EDatasetExecutionStatus.PENDING) {
      setAllowCancelOption(true)
      setStatusColor(ARGOS_PENDING_COLOR)
    } else if (selectedDatasetExecutionStatus === EDatasetExecutionStatus.IN_PROGRESS) {
      setAllowCancelOption(true)
      setStatusColor(ARGOS_PROGRESS_COLOR)
    } else if (selectedDatasetExecutionStatus === EDatasetExecutionStatus.ABORTED) {
      setAllowCancelOption(false)
      setStatusColor('red')
    } else if (selectedDatasetExecutionStatus === EDatasetExecutionStatus.COMPLETED) {
      setAllowCancelOption(false)
      setStatusColor('green')
    } else if (selectedDatasetExecutionStatus === EDatasetExecutionStatus.INVALID_RESULTS) {
      setAllowCancelOption(false)
      setStatusColor('orange')
    } else if (selectedDatasetExecutionStatus === EDatasetExecutionStatus.NOT_STARTED) {
      setAllowCancelOption(false)
      setStatusColor('')
    }
  }

  const OutlinedBox = (props: any) => {
    const {
      label,
      children
    } = props;
    return (
      <Box>
        <FormLabel
          sx={{
            marginLeft: "2em",
            marginTop: "-0.15em",
            paddingLeft: "0.50em",
            paddingRight: '0.44em',
            zIndex: 2,
            backgroundColor: 'background.paper',
            position: "absolute",
            fontSize: "0.75em",
            width: 'auto',
            color: statusColor,
          }}>{label}</FormLabel>
        <Box
          sx={{
            position: 'relative',
            borderRadius: theme => theme.shape.borderRadius + 'px',
            fontSize: '0.875rem',
          }}
        >
          <Box
            sx={{
              padding: (theme) => theme.spacing(1),
              display: 'flex',
              gap: (theme) => theme.spacing(1),
              flexWrap: 'wrap',
              overflow: 'auto'
            }}
          >
            {children}
          </Box>
          <fieldset aria-hidden={"true"} style={{
            textAlign: 'left',
            position: 'absolute',
            bottom: 0,
            right: 0,
            top: '-5px',
            left: 0,
            margin: 0,
            padding: '0 8px',
            pointerEvents: 'none',
            borderRadius: 'inherit',
            borderStyle: 'solid',
            borderWidth: '1px',
            overflow: 'hidden',
            minWidth: '0%',
            borderColor: 'rgba(255, 255, 255, 0.23)',
          }}
          >
            <legend style={{
              float: 'unset',
              overflow: 'hidden',
              display: 'block',
              width: 'auto',
              padding: 0,
              height: '11px',
              fontSize: '0.75em',
              visibility: 'hidden',
              maxWidth: '100%',
              transition: 'max-width 100ms cubic-bezier(0.0, 0, 0.2, 1) 50ms',
              whiteSpace: 'nowrap',
            }}><span>{label}</span></legend>
          </fieldset>
        </Box>
      </Box>
    );
  }


  const displayedOwnerValue = didFailToLoadOwner ? 'Failed to load owner' : ownerNotFound ? 'Owner not found' : owner

  return <>
    {props?.datasetType == SINGLE_DATASET_TYPE_STR &&
      <>
        <ArgosFlexbox vertical style={{width: '100%'}}>
          <ArgosFlexbox style={{alignItems: "center", justifyContent: "space-between"}}>
            <Breadcrumbs style={{fontSize: "20px", color: headerGray}}
                         aria-label="breadcrumb">
              {breadcrumbs &&
                breadcrumbs.map((b, index) =>
                  index !== breadcrumbs.length - 1 ? (
                    <Typography
                      style={{
                        fontSize: "20px",
                        color: headerGray
                      }}
                      key={b.title}
                    >
                      {b.title}
                    </Typography>
                  ) : (
                    <Typography
                      style={{
                        fontSize: "20px",
                        fontWeight: "bold",
                        color: headerGray
                      }}
                      key={b.title}
                    >
                      {b.title}
                    </Typography>
                  ),
                )
              }
            </Breadcrumbs>



            <Typography
              style={{
                fontSize: "15px",
                // padding: '10px',
                minWidth: 150
              }}
              color="GrayText"
              key={owner}
            >
              <ArgosFlexbox>
                <span>Owner:</span>
                <span><Loader loaderSize={20} isLoading={isLoadingOwner}>{displayedOwnerValue}</Loader></span>
              </ArgosFlexbox>
            </Typography>
          </ArgosFlexbox>
          <ArgosFlexbox style={{justifyContent: "space-between", alignItems: 'flex-end', margin: "10px 0 30px 5px"}}>
            <OutlinedBox label={statusTitle}>
              <ArgosFlexbox vertical style={{
                alignItems: "center",
                justifyContent: 'flex-end',
                gap: "15px",
                outline: 'solid',
                padding: '10px',
                outlineWidth: '2px',
                outlineColor: theme.layout.header.backgroundColor,
                borderRadius: 10,
                height: '65px',
                width: '445px'
              }}>
                <ArgosFlexbox>
                  <ArgosFlexbox>
                    <Loader isLoading={generateDatasetProgressData.shouldReplaceExecutionStatusWithLoader}>
                      {
                        //Below setting condition selectedDatasetExecutionStatus !== EDatasetExecutionStatus.PENDING to show loader and 'X' Cancel the other calculations not needed
                        <ArgosFlexbox vertical style={{justifyContent: "center", alignItems: 'center'}}>
                          {
                            <div>
                              <ArgosFlexbox>
                                <Typography
                                  style={{
                                    fontSize: "15px",
                                    color: theme.layout.header.backgroundColor,
                                  }}
                                  variant="body2" color="textSecondary">
                                  {
                                    (selectedDatasetExecutionStatus == EDatasetExecutionStatus.PENDING || currentDatasetLocalState?.totalSteps == null || currentDatasetLocalState?.totalSteps == 0) ?
                                      <Loader isLoading={activeLoader}/> :
                                      currentDatasetLocalState?.fraction + ' ' ?? ' '
                                  }
                                </Typography>
                                <Typography
                                  style={{fontSize: "15px", color: theme.layout.header.backgroundColor}}
                                  variant="body2"
                                  color="textSecondary">
                                  {
                                    (selectedDatasetExecutionStatus !== EDatasetExecutionStatus.PENDING && currentDatasetLocalState?.progress) ? '(' + `${currentDatasetLocalState.progress}%` + ')' : ' '
                                  }
                                </Typography>
                              </ArgosFlexbox>
                            </div>
                          }
                          {selectedDatasetExecutionStatus !== EDatasetExecutionStatus.PENDING && <ArgosFlexbox style={{justifyContent: "left", alignItems: 'center'}}>
                            <Box width="100%" mr={1}>
                              <LinearProgress
                                style={{width: "45ch"}}
                                variant={'determinate'}
                                value={currentDatasetLocalState?.progress ?? 0}
                                color={'primary'}
                              />
                            </Box>
                          </ArgosFlexbox>}
                        </ArgosFlexbox>
                      }
                    </Loader>
                  </ArgosFlexbox>
                  <Box minWidth={35}>
                    {allowCancelOption && <div>
                      {hasWriteAccess && <Tooltip title="Cancel Job Execution" placement='bottom-end'>
                        <IconButton disabled={!allowCancel}
                                    onClick={() => {
                                      cancelRun(props.datasetId)
                                    }}
                                    sx={{
                                      display: allowCancel ? "" : "none",
                                      margin: '0'
                                    }}>
                          <CloseIcon sx={{color: "red"}}/>
                        </IconButton>
                      </Tooltip>}
                    </div>}
                  </Box>
                </ArgosFlexbox>
              </ArgosFlexbox>
            </OutlinedBox>
            <ArgosFlexbox
              onMouseEnter={handlePopoverOpen}
              onMouseLeave={handlePopoverClose}
              style={{alignItems: "right", gap: "5px", height: 'fit-content'}}>

              {Object.values(doeMinMaxViolations).length > 0 && <PriorityHighIcon color="error"/>}

              <ArgosButton
                disabled={isReadOnlyStudy || isCoverageRequirementsSaved || Object.values(doeMinMaxViolations).length > 0}
                color={(isReadOnlyStudy || isCoverageRequirementsSaved) ? "inherit" : "primary"} onClick={() => {
                props?.saveToDatabase()
              }}>
                Save {VAR_COMMON_CONSTANTS.VAR_DATA_SET} Configuration
              </ArgosButton>

              {props?.executionStatus == "Completed" &&
                <IconButton color='primary' title={'Download '+VAR_COMMON_CONSTANTS.VAR_DATA_SET+' as .CSV'}
                            onClick={() => handleOpenDownloadAsCSV()}>
                  <DownloadIcon/>
                </IconButton>
              }
              <div style={{marginLeft: 'auto'}}>
                {favoriteIcon}
              </div>
            </ArgosFlexbox>
            {Object.values(doeMinMaxViolations).length > 0 && <Popover
              id="mouse-over-popover"
              sx={{
                pointerEvents: 'none',
              }}
              open={open}
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              onClose={handlePopoverClose}
              disableRestoreFocus
            >
              {Object.values(doeMinMaxViolations).map((issue: string) => <Typography
                sx={{p: 1}}>{issue}</Typography>)}
            </Popover>}
          </ArgosFlexbox>
        </ArgosFlexbox>
      </>
    }
    {
      props.datasetType == MERGED_DATASET_TYPE_STR &&
        <ArgosFlexbox vertical style={{width: '100%', justifyContent: 'right'}}>
          <ArgosFlexbox style={{alignItems: "center", justifyContent: "space-between"}}>
            <Breadcrumbs style={{fontSize: "20px", color: headerGray}}
                         aria-label="breadcrumb">
              {breadcrumbs &&
                breadcrumbs.map((b, index) =>
                  index !== breadcrumbs.length - 1 ? (
                    <Typography
                      style={{
                        fontSize: "20px",
                        color: headerGray
                      }}
                      key={b.title}
                    >
                      {b.title}
                    </Typography>
                  ) : (
                    <Typography
                      style={{
                        fontSize: "20px",
                        fontWeight: "bold",
                        color: headerGray
                      }}
                      key={b.title}
                    >
                      {b.title}
                    </Typography>
                  ),
                )
              }
            </Breadcrumbs>
            <Typography
              style={{
                fontSize: "15px",
                padding: '10px'
              }}
              color="GrayText"
              key={owner}
            >
              Owner: {owner}
            </Typography>
          </ArgosFlexbox>
          <ArgosFlexbox style={{justifyContent: 'right', alignItems: "center", width: '100%'}}>
            <IconButton color='primary' title={'Download ' + VAR_COMMON_CONSTANTS.VAR_DATA_SET + ' as .CSV'} onClick={handleOpenDownloadAsCSV}>
              <DownloadIcon/>
            </IconButton>
            <div>
              {favoriteIcon}
            </div>
          </ArgosFlexbox>
        </ArgosFlexbox>
    }
    <StyledArgosDialog
      width={'fit-content'}
      height={'fit-content'}
      open={isDownloadAsCSVDialogOpen}
      onClose={() => {
        // dispatch(setIsDownloadAsCSVDialogOpen(false))
      }}
    >
      <DownloadDatasetsDialog datasetType={props.datasetType} downloadDatasetAsCSV={downloadDatasetAsCSV} onClose={onClose} breadcrumbs={paths}/>
    </StyledArgosDialog>
  </>
}

export default React.memo(DatasetControlPanel)
